import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, createContext } from "react";
import * as PusherPushNotifications from "@pusher/push-notifications-web";
import { AppContext } from "./AppContext";
import useHttp from "../hooks/useHttp";

const BeamsContext = createContext();
export const useBeams = () => useContext(BeamsContext);

export const BeamsProvider = React.memo(({ children }) => {
  const [appProperties] = useContext(AppContext);
  const { fetchData } = useHttp();
  const beamsClientRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState("default");
  const connectionProperties = useRef({
    userId: null,
    token: null,
    isRegistering: false, // Flag to prevent concurrent registration attempts
  });

  const getCurrentUserId = useCallback(() => connectionProperties.current.userId, []);

  const currentUserId = useMemo(() => {
    return appProperties?.currentUser?.osyncUserId;
  }, [appProperties]);


  // Check notification permission status
  const checkPermission = useCallback(() => {
    if ("Notification" in window) {
      setPermissionStatus(Notification.permission);
      return Notification.permission;
    }
    return "unsupported";
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications.");
      return false;
    }

    if (Notification.permission === "granted") {
      setPermissionStatus("granted");
      return true;
    }

    if (Notification.permission === "denied") {
      setPermissionStatus("denied");
      console.warn("Notification permission has been denied.");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      return permission === "granted";
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }, []);

  // Initialize Beams client
  const initializeBeams = useCallback(async () => {
    try {
      const instanceId = process.env.REACT_APP_PUSHER_BEAMS_INSTANCE_ID;
      if (!instanceId) {
        console.warn("Pusher Beams instance ID is not configured.");
        return false;
      }

      // Check if already initialized
      if (beamsClientRef.current) {
        console.log("Beams client already initialized");
        return true;
      }

      // In initializeBeams
      const registration = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;
      const beamsClient = new PusherPushNotifications.Client({
        instanceId: instanceId,
        serviceWorkerRegistration: registration,
      });

      beamsClientRef.current = beamsClient;

      // Start the client
      await beamsClient.start();
      setIsInitialized(true);
      console.log("Beams client initialized successfully");
      return true;
    } catch (error) {
      console.error("Error initializing Beams client:", error);
      setIsInitialized(false);
      return false;
    }
  }, []);

  const registerUser = useCallback(
    async (userId) => {
      console.log("Beams: registerUser called for:", userId);

      if (connectionProperties.current.isRegistering) {
        console.log("Beams: Registration already in progress, skipping...");
        return false;
      }

      if (connectionProperties.current.userId === userId && isRegistered) {
        return true;
      }

      try {
        connectionProperties.current.isRegistering = true;

        if (!beamsClientRef.current || !isInitialized) {
          console.warn("Beams: client not initialized. Initializing now...");
          const initialized = await initializeBeams();
          if (!initialized) {
            connectionProperties.current.isRegistering = false;
            return false;
          }
        }

        if (!userId) {
          console.warn("Cannot register user: userId is required");
          connectionProperties.current.isRegistering = false;
          return false;
        }

        if (isRegistered) {
          return true;
        }

        const hasPermission = await requestPermission();
        if (!hasPermission) {
          console.warn("Notification permission not granted. Cannot register user.");
          connectionProperties.current.isRegistering = false;
          return false;
        }

        const tokenProvider = {
          fetchToken: async (providedUserId) => {
            const targetUserId = providedUserId ?? userId;

            if (!targetUserId) {
              throw new Error("PusherBeams: userId is missing");
            }

            try {
              const response = await fetchData(`pusher/beams-auth?osyncUserId=${encodeURIComponent(targetUserId)}`, "GET", null, appProperties);

              const parsed = typeof response === "string" ? JSON.parse(response) : response;

              const token = parsed?.data?.token ?? parsed?.token;

              if (!token || typeof token !== "string") {
                throw new Error("PusherBeams: Invalid token received from server");
              }

              return { token };
            } catch (error) {
              console.error("PusherBeams: fetchToken failed", error);
              throw error;
            }
          },
        };

        try {
          await beamsClientRef.current.setUserId(userId, tokenProvider);
        } catch (error) {
          // If the error is about changing userId not being allowed, clear state and retry
          if (error.message && error.message.includes("Changing the `userId` is not allowed")) {
            console.log("Beams: setUserId failed - userId already set. Clearing state and retrying...");
            try {
              await beamsClientRef.current.clearAllState();
              connectionProperties.current.userId = null;
              connectionProperties.current.token = null;
              setIsRegistered(false);
              // Retry setUserId after clearing
              await beamsClientRef.current.setUserId(userId, tokenProvider);
              console.log("debug >> setUserId completed successfully after retry");
            } catch (retryError) {
              console.error("debug >> Error in setUserId retry:", retryError);
              throw retryError;
            }
          } else {
            console.error("debug >> Error in setUserId:", error);
            throw error;
          }
        }

        connectionProperties.current.userId = userId;
        // Token is fetched dynamically via TokenProvider, so we don't store it here
        connectionProperties.current.token = null;
        setIsRegistered(true);

        return true;
      } catch (error) {
        console.error("Error registering user with Beams:", error);
        setIsRegistered(false);
        return false;
      } finally {
        // Always clear the registration flag
        connectionProperties.current.isRegistering = false;
      }
    },
    [isInitialized, isRegistered, initializeBeams, requestPermission, appProperties, fetchData],
  );

  // Unregister user from Beams
  const unregisterUser = useCallback(async () => {
    try {
      if (beamsClientRef.current && isRegistered) {
        await beamsClientRef.current.clearAllState();
        connectionProperties.current.userId = null;
        connectionProperties.current.token = null;
        setIsRegistered(false);
        console.log("User unregistered from Beams");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error unregistering user from Beams:", error);
      return false;
    }
  }, [isRegistered]);


  
  useEffect(() => {

    // Only register if we have a userId, not already registered, and not currently registering
    if (currentUserId && !isRegistered && connectionProperties.current.userId !== currentUserId && !connectionProperties.current.isRegistering) {

      initializeBeams().then((initialized) => {
        if (initialized) {
          console.log("Beams: Adding device interest");
          registerUser(currentUserId).then(() => {
            console.log("Beams: Registered user with Beams");
          });
        }
      });
    }
  }, [currentUserId, isRegistered, initializeBeams, registerUser]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  const beamsContext = useMemo(
    () => ({
      beamsClient: beamsClientRef.current,
      isInitialized,
      isRegistered,
      permissionStatus,
      getCurrentUserId,
      initializeBeams,
      registerUser,
      unregisterUser,
      requestPermission,
      checkPermission,
      connectionProperties: connectionProperties.current,
    }),
    [
      isInitialized,
      isRegistered,
      permissionStatus,
      getCurrentUserId,
      initializeBeams,
      registerUser,
      unregisterUser,
      requestPermission,
      checkPermission,
    ],
  );
  console.log("ddddd beamsContext >>> ", beamsContext);
  return <BeamsContext.Provider value={beamsContext}>{children}</BeamsContext.Provider>;
});
